import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Search, Eye, CheckCircle, XCircle, AlertTriangle, Clock, Calendar, CheckCircle2, DollarSign, Archive, ArchiveRestore } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const AdminSubmissions = () => {
  const { token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, submission: null });
  const [archiveDialog, setArchiveDialog] = useState({ open: false, submission: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, submission: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [resubmissionRequired, setResubmissionRequired] = useState(false);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
      setFilteredSubmissions(response.data);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate proof-of-play completeness (5 fields now)
  const getCompleteness = (submission) => {
    const hasFiles = (val) => Array.isArray(val) ? val.length > 0 : !!val;
    const fields = [
      hasFiles(submission.merchandise_url),
      hasFiles(submission.proof_of_play_url),
      hasFiles(submission.bottle_console_url),
      hasFiles(submission.branding_stories_post_url),
      submission.track_play_count > 0
    ];
    const completed = fields.filter(Boolean).length;
    return { completed, total: 5, isComplete: completed === 5 };
  };

  // Calculate deadline status
  const getDeadlineStatus = (submission) => {
    if (!submission.submission_deadline) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(submission.submission_deadline);
    deadline.setHours(0, 0, 0, 0);
    const submissionDate = submission.created_at ? new Date(submission.created_at) : null;
    
    const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    
    if (submissionDate && submissionDate <= deadline) {
      return { status: "on_time", label: "On Time", daysUntilDeadline };
    } else if (daysUntilDeadline < 0) {
      return { status: "overdue", label: `${Math.abs(daysUntilDeadline)} days overdue`, daysUntilDeadline };
    } else if (daysUntilDeadline <= 3) {
      return { status: "due_soon", label: `Due in ${daysUntilDeadline} days`, daysUntilDeadline };
    }
    return { status: "on_track", label: `${daysUntilDeadline} days left`, daysUntilDeadline };
  };

  useEffect(() => {
    fetchSubmissions();
  }, [token]);

  useEffect(() => {
    let filtered = submissions;
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(s => 
        s.artist_name.toLowerCase().includes(searchLower) ||
        s.venue.toLowerCase().includes(searchLower) ||
        s.city.toLowerCase().includes(searchLower)
      );
    }
    
    if (statusFilter === "archived") {
      filtered = filtered.filter(s => s.archived);
    } else {
      filtered = filtered.filter(s => !s.archived);
      if (statusFilter !== "all") {
        // Handle backwards compatibility for old status values
        if (statusFilter === "pending_agency_approval") {
          filtered = filtered.filter(s => 
            s.status === "pending_agency_approval" || 
            s.status === "pending_approval" || 
            s.status === "submitted"
          );
        } else {
          filtered = filtered.filter(s => s.status === statusFilter);
        }
      }
    }
    
    setFilteredSubmissions(filtered);
  }, [search, statusFilter, submissions]);

  const handleApprove = async () => {
    if (!actionDialog.submission) return;
    
    try {
      await axios.put(
        `${API}/admin/submissions/${actionDialog.submission.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission approved and sent to brand for review!");
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve submission");
    } finally {
      setActionDialog({ open: false, type: null, submission: null });
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.submission || !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/submissions/${rejectDialog.submission.id}/reject`,
        { 
          rejection_reason: rejectionReason,
          resubmission_required: resubmissionRequired
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission rejected");
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject submission");
    } finally {
      setRejectDialog({ open: false, submission: null });
      setRejectionReason("");
      setResubmissionRequired(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveDialog.submission) return;
    try {
      await axios.put(
        `${API}/admin/submissions/${archiveDialog.submission.id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission archived. You can restore it anytime from the Archived filter.");
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive submission");
    } finally {
      setArchiveDialog({ open: false, submission: null });
    }
  };

  const handleRestore = async (submission) => {
    try {
      await axios.put(
        `${API}/admin/submissions/${submission.id}/unarchive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission restored to the active list");
      fetchSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore submission");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-500/20 text-gray-600",
      submitted: "bg-orange-500/20 text-orange-600",
      pending_approval: "bg-orange-500/20 text-orange-600",
      pending_agency_approval: "bg-orange-500/20 text-orange-600",
      pending_brand_approval: "bg-purple-500/20 text-purple-600",
      approved: "bg-green-500/20 text-green-600",
      rejected: "bg-[#ED5A34]/20 text-[#ED5A34]"
    };
    const labels = {
      draft: "Draft",
      submitted: "Pending Agency",
      pending_approval: "Pending Agency",
      pending_agency_approval: "Pending Agency",
      pending_brand_approval: "Pending Brand",
      approved: "Approved",
      rejected: "Rejected"
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-wider ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPayoutBadge = (payoutStatus) => {
    const s = payoutStatus || "pending";
    const styles = {
      pending: "bg-gray-100 text-gray-600",
      processing: "bg-amber-100 text-amber-700",
      paid: "bg-green-100 text-green-700"
    };
    const labels = { pending: "Pending", processing: "Processing", paid: "Paid" };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold uppercase tracking-wider rounded ${styles[s] || styles.pending}`}>
        <DollarSign size={12} />
        {labels[s] || s}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-submissions">
        <div className="mb-8">
          <h1 className="text-4xl font-heading text-gray-900 mb-2">SUBMISSIONS</h1>
          <p className="text-gray-600">Review and approve campaign submissions from artists</p>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search by artist, venue, or city..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 border-gray-300"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-56 h-12" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending_agency_approval">Pending Agency Review</SelectItem>
                <SelectItem value="pending_brand_approval">Pending Brand Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading submissions...</div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {search || statusFilter !== "all" ? "No matching submissions found" : "No submissions yet"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="kf-table">
                <thead>
                  <tr className="bg-gray-50">
                    <th>Artist</th>
                    <th>City</th>
                    <th>Venue</th>
                    <th>Event Date</th>
                    <th>Deadline</th>
                    <th>Completeness</th>
                    <th>Status</th>
                    <th>Payout</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map((submission) => {
                    const completeness = getCompleteness(submission);
                    const deadlineStatus = getDeadlineStatus(submission);
                    
                    return (
                      <tr key={submission.id} className="hover:bg-gray-50">
                        <td>
                          <div>
                            <p className="font-medium text-gray-900">{submission.artist_name}</p>
                            <p className="text-xs text-gray-500">{submission.artist_email}</p>
                          </div>
                        </td>
                        <td className="text-gray-600">{submission.city}</td>
                        <td className="text-gray-600">{submission.venue}</td>
                        <td className="text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar size={14} className="text-gray-400" />
                            {submission.date}
                          </div>
                        </td>
                        <td>
                          {deadlineStatus ? (
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                              deadlineStatus.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              deadlineStatus.status === 'due_soon' ? 'bg-orange-100 text-orange-700' :
                              deadlineStatus.status === 'on_time' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {deadlineStatus.status === 'overdue' ? <AlertTriangle size={12} /> :
                               deadlineStatus.status === 'on_time' ? <CheckCircle2 size={12} /> :
                               <Clock size={12} />}
                              {deadlineStatus.label}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">N/A</span>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            {completeness.isComplete ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 bg-green-100 text-green-700 rounded">
                                <CheckCircle2 size={12} />
                                Complete
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 bg-orange-100 text-orange-700 rounded">
                                <AlertTriangle size={12} />
                                {completeness.completed}/{completeness.total}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>{getStatusBadge(submission.status)}</td>
                        <td>{getPayoutBadge(submission.payout_status)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Link 
                              to={`/admin/submissions/${submission.id}`}
                              className="inline-flex items-center gap-1 text-[#00B4E5] hover:underline text-sm font-medium"
                              data-testid={`view-submission-${submission.id}`}
                            >
                              <Eye size={16} />
                              View
                            </Link>
                            {(submission.status === "pending_agency_approval" || submission.status === "pending_approval" || submission.status === "submitted") && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-500 hover:bg-green-600 text-white h-8 px-3"
                                  onClick={() => setActionDialog({ open: true, type: "approve", submission })}
                                  data-testid={`approve-${submission.id}`}
                                >
                                  <CheckCircle size={14} className="mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-[#ED5A34] text-[#ED5A34] hover:bg-[#ED5A34] hover:text-white h-8 px-3"
                                  onClick={() => setRejectDialog({ open: true, submission })}
                                  data-testid={`reject-${submission.id}`}
                                >
                                  <XCircle size={14} className="mr-1" />
                                  Reject
                                </Button>
                              </>
                            )}
                            {submission.archived ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-gray-300 text-gray-600 hover:bg-gray-100 h-8 px-3"
                                onClick={() => handleRestore(submission)}
                                data-testid={`restore-${submission.id}`}
                              >
                                <ArchiveRestore size={14} className="mr-1" />
                                Restore
                              </Button>
                            ) : submission.status === "draft" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-gray-400 hover:text-gray-700 h-8 px-2"
                                onClick={() => setArchiveDialog({ open: true, submission })}
                                data-testid={`archive-${submission.id}`}
                                title="Archive this draft"
                              >
                                <Archive size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredSubmissions.length} of {submissions.length} submissions
        </div>

        {/* Approval Dialog */}
        <AlertDialog open={actionDialog.open && actionDialog.type === "approve"} onOpenChange={(open) => !open && setActionDialog({ open: false, type: null, submission: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Approve Submission</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to approve the submission from {actionDialog.submission?.artist_name}? This will send it to the Brand for final approval.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleApprove}
                className="bg-green-500 hover:bg-green-600"
              >
                Approve & Send to Brand
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Archive Dialog */}
        <AlertDialog open={archiveDialog.open} onOpenChange={(open) => !open && setArchiveDialog({ open: false, submission: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Archive Draft Submission</AlertDialogTitle>
              <AlertDialogDescription>
                Archive the draft from {archiveDialog.submission?.artist_name}{archiveDialog.submission?.venue ? ` (${archiveDialog.submission.venue})` : ""}? 
                Nothing is deleted — it will be hidden from the active list and can be restored anytime from the "Archived" filter.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleArchive} data-testid="confirm-archive-button">
                Archive
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Rejection Dialog with Reason */}
        <Dialog open={rejectDialog.open} onOpenChange={(open) => {
          if (!open) {
            setRejectDialog({ open: false, submission: null });
            setRejectionReason("");
            setResubmissionRequired(false);
          }
        }}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-[#ED5A34]">Reject Submission</DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting the submission from <span className="font-semibold">{rejectDialog.submission?.artist_name}</span>. This will be visible to the artist.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="rejection-reason">Rejection Reason *</Label>
                <Textarea
                  id="rejection-reason"
                  placeholder="Enter the reason for rejection (e.g., Incomplete proof, Poor quality, Wrong format...)"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="min-h-[100px]"
                  data-testid="rejection-reason-input"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-1">
                  <Label htmlFor="resubmission-toggle" className="font-medium">Resubmission Required</Label>
                  <p className="text-sm text-gray-500">Allow the artist to resubmit with corrections</p>
                </div>
                <Switch
                  id="resubmission-toggle"
                  checked={resubmissionRequired}
                  onCheckedChange={setResubmissionRequired}
                  data-testid="resubmission-toggle"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setRejectDialog({ open: false, submission: null });
                setRejectionReason("");
                setResubmissionRequired(false);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="bg-[#ED5A34] hover:bg-[#d44a28] text-white"
                data-testid="confirm-reject-button"
              >
                Reject Submission
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminSubmissions;
